// ──────────────────────────────────────────────────────────────────────────
// Agent-Request V2 figure + period formatting (design doc 01, Card & Feed).
//
// Since Marin's 2026-07-21 decision the REAL differentiated figures are public
// on every request surface — the budget band is dead in the UI. This util is
// the single formatter the card, the list row and the loupe all share so the
// three surfaces can never drift:
//   • compact money            "£4.5M", "€250k", "£90k - 140k"
//   • fin-box rows             Transfer fee / Loan fee / Salary, exactly as
//                              captured (net·gross, range·fixed, currency);
//                              empty lines don't render at all
//   • commission three-state   split ("50 / 50") / "Negotiable" / no-commission
//   • period line              players "For Summer 26" (target_window_label),
//                              staff "Starts immediately"/"From September 26",
//                              trial "Trial · Aug 3 - Aug 21"; hidden when unset
//   • spec chips               "Age 21-26", "Left foot", "≥ 1.83 m", "EU only",
//                              "≥ 5 yrs experience", "12-month contract"
//
// All exports are pure functions (no React) — unit-tested in isolation.
// ──────────────────────────────────────────────────────────────────────────

import { CURRENCY_SYMBOLS } from "./currencyFormatter";
import { shouldRender } from "./tenderFormatters";

// Numeric guard: the V2 serializers send JSON numbers, but older payloads may
// carry decimal strings — accept both, reject empty/NaN/negative-garbage.
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// "4500000" → "4.5M", "250000" → "250k", "800" → "800". One decimal max,
// trailing zero trimmed — the design's compact convention ("£4.5M", "£90k").
export const compactNumber = (value) => {
  const n = num(value);
  if (n === null) return null;
  const compact = (v, suffix) => {
    const r = Math.round(v * 10) / 10;
    return `${Number.isInteger(r) ? r.toFixed(0) : r}${suffix}`;
  };
  const abs = Math.abs(n);
  if (abs >= 1e6) return compact(n / 1e6, "M");
  if (abs >= 1e3) return compact(n / 1e3, "k");
  return `${n}`;
};

// "£4.5M" — currency symbol + compact number. Null when there is no value.
export const formatCompactMoney = (value, currency = "USD") => {
  const n = compactNumber(value);
  if (n === null) return null;
  return `${CURRENCY_SYMBOLS[currency] || ""}${n}`;
};

// "£90k - 140k" (symbol only on the first bound, per the design examples),
// "From £90k" / "Up to £140k" when one bound is missing, null when both are.
export const formatCompactMoneyRange = (from, to, currency = "USD") => {
  const a = compactNumber(from);
  const b = compactNumber(to);
  const sym = CURRENCY_SYMBOLS[currency] || "";
  if (a !== null && b !== null) return `${sym}${a} - ${b}`;
  if (a !== null) return `From ${sym}${a}`;
  if (b !== null) return `Up to ${sym}${b}`;
  return null;
};

// Salary exactly as captured: net·gross × range·fixed. `unit` is "mo" on the
// card/list and "month" in the loupe (the design's two salary suffixes).
// Returns "£90k - 140k gross / mo" or null when no salary info exists.
export const formatSalaryLine = (tender, unit = "mo") => {
  if (!tender) return null;
  const currency = tender.currency || "USD";
  const suffix = (kind, v) => (v ? `${v} ${kind} / ${unit}` : null);
  if (tender.salary_type === "fixed_amount") {
    if (num(tender.fix_net_salary) !== null)
      return suffix("net", formatCompactMoney(tender.fix_net_salary, currency));
    if (num(tender.fix_gross_salary) !== null)
      return suffix("gross", formatCompactMoney(tender.fix_gross_salary, currency));
    return null;
  }
  if (tender.salary_type === "salary_between") {
    const net = formatCompactMoneyRange(
      tender.net_salary_from,
      tender.net_salary_to,
      currency,
    );
    if (net) return suffix("net", net);
    const gross = formatCompactMoneyRange(
      tender.gross_salary_from,
      tender.gross_salary_to,
      currency,
    );
    return suffix("gross", gross);
  }
  return null;
};

// The fin-box rows: Transfer fee / Loan fee / Salary. Empty lines don't
// render; an empty array means the box shows "Conditions negotiable".
export const buildFinRows = (tender, unit = "mo") => {
  if (!tender) return [];
  const currency = tender.currency || "USD";
  const rows = [];
  if (tender.type_of_tender === "buy") {
    const fee = formatCompactMoney(tender.maximum_transfer_fee, currency);
    if (fee && num(tender.maximum_transfer_fee) > 0)
      rows.push({ key: "transfer_fee", label: "Transfer fee", value: `up to ${fee}` });
  }
  if (tender.type_of_tender === "loan") {
    const amount = num(tender.loan_fee_amount);
    // EXPLICIT 0 (number 0 / "0" — never null/undefined/"") is a captured
    // answer, not an absent one: the agent ticked "loan fee" and typed 0.
    // That renders as "Loan fee: Free" on card, list and loupe alike;
    // an absent amount stays hidden as before (2026-07-22 follow-up item 7).
    if (amount === 0) {
      rows.push({ key: "loan_fee", label: "Loan fee", value: "Free" });
    } else if (tender.has_loan_fee && amount !== null && amount > 0) {
      rows.push({
        key: "loan_fee",
        label: "Loan fee",
        value: formatCompactMoney(amount, currency),
      });
    }
  }
  const salary = formatSalaryLine(tender, unit);
  if (salary) rows.push({ key: "salary", label: "Salary", value: salary });
  return rows;
};

// Commission three-state — never silently blank (design rule 3):
//   { state: "none" }                    → amber "No commission for this job"
//   { state: "split", value: "50 / 50" } → the split as captured
//   { state: "negotiable" }              → "Negotiable"
// When BOTH split and note are set, the note rides along as `note` so the
// loupe can show it under the split instead of swallowing it (F8).
export const getCommission = (tender) => {
  if (tender?.no_commission) return { state: "none", value: null, note: null };
  const note =
    (tender?.commission_split_note && tender.commission_split_note.trim()) || "";
  const split =
    (tender?.commission_split && tender.commission_split.trim()) || note || "";
  if (split)
    return { state: "split", value: split, note: split !== note ? note || null : null };
  return { state: "negotiable", value: null, note: null };
};

// "2026-09" → "September 26" (staff start month, design "From September 26").
const monthYearLabel = (yyyyMm) => {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(String(yyyyMm))) return null;
  const [y, m] = String(yyyyMm).split("-").map(Number);
  if (m < 1 || m > 12) return null;
  const month = new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  return `${month} ${y % 100}`;
};

// One trial date → "Aug 3" (design's trial meta; UTC so date-only strings
// never drift a day, mirroring formatWindowDate in agentTenderChoices).
const trialDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const month = d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  return `${month} ${d.getUTCDate()}`;
};

// The period line (design item 4). Hidden (null) when nothing is set.
//   player, trial   → "Trial · Aug 3 - Aug 21"
//   player, window  → "For Summer 26"        (target_window_label)
//   staff           → "Starts immediately" / "From September 26"
export const getPeriodLine = (tender) => {
  if (!tender) return null;
  if (tender.tender_category === "staff") {
    if (tender.staff_start_type === "immediately") return "Starts immediately";
    if (tender.staff_start_type === "from_month") {
      const label = monthYearLabel(tender.staff_start_month);
      return label ? `From ${label}` : null;
    }
    return null;
  }
  if (tender.type_of_tender === "trial") {
    const a = trialDate(tender.trial_period_start_date);
    const b = trialDate(tender.trial_period_end_date);
    if (a && b) return `Trial · ${a} - ${b}`;
    if (a || b) return `Trial · ${a || b}`;
    // fall through: a trial may still carry a target window
  }
  const label =
    typeof tender.target_window_label === "string"
      ? tender.target_window_label.trim()
      : "";
  return label ? `For ${label}` : null;
};

// "≥ 1.83 m" (card chip) / "1.83 m" (loupe cell) from a cm integer.
export const formatHeightMeters = (cm, { gte = false } = {}) => {
  const n = num(cm);
  if (n === null || n <= 0) return null;
  const m = (n / 100).toFixed(2);
  return gte ? `≥ ${m} m` : `${m} m`;
};

// "Age 21-26" / "Age ≥ 21" / "Age ≤ 30" from the (string) age bounds.
export const formatAgeChip = (minAge, maxAge) => {
  const a = num(minAge);
  const b = num(maxAge);
  if (a !== null && a > 0 && b !== null && b > 0) return `Age ${a}-${b}`;
  if (a !== null && a > 0) return `Age ≥ ${a}`;
  if (b !== null && b > 0) return `Age ≤ ${b}`;
  return null;
};

// "12-month contract" from temporary_duration + unit (design staff chip).
export const formatDurationChip = (tender) => {
  const n = num(tender?.temporary_duration);
  if (n === null || n <= 0) return null;
  const unit = { weeks: "week", months: "month", years: "year" }[
    tender?.temporary_duration_unit
  ] || "month";
  return `${n}-${unit} contract`;
};

// Spec chips for the card (design item: chips row under the country line).
// "Only free players" is NOT here — it renders as the special info chip.
export const getSpecChips = (tender) => {
  if (!tender) return [];
  const chips = [];
  const age = formatAgeChip(tender.min_age, tender.max_age);
  if (age) chips.push(age);
  if (tender.foot === "L") chips.push("Left foot");
  if (tender.foot === "R") chips.push("Right foot");
  const height = formatHeightMeters(tender.minimum_height, { gte: true });
  if (height) chips.push(height);
  if (tender.only_eu_player) chips.push("EU only");
  if (tender.type_of_tender === "loan" && tender.loan_with_option_to_buy)
    chips.push("Option to buy");
  if (tender.tender_category === "staff" && !tender.experience_not_important) {
    const years = num(tender.experience_min_years);
    if (years !== null && years > 0) chips.push(`≥ ${years} yrs experience`);
  }
  const duration = formatDurationChip(tender);
  if (duration) chips.push(duration);
  return chips;
};

// Loupe market-value cell: "£7M - 10M" / "£2.5M" / "Not important" / null.
export const getMarketValueLine = (tender) => {
  if (!tender) return null;
  const currency = tender.currency || "USD";
  if (tender.market_value_type === "not_important") return "Not important";
  if (tender.market_value_type === "market_value_range") {
    return formatCompactMoneyRange(
      tender.market_value_from,
      tender.market_value_to,
      currency,
    );
  }
  if (num(tender.fix_market_value) !== null && num(tender.fix_market_value) > 0)
    return formatCompactMoney(tender.fix_market_value, currency);
  return null;
};

// Loupe games-played cell: "≥ 15 · last season" (design). Unset/0 = hidden.
export const getGamesPlayedLine = (tender) => {
  const season = num(tender?.matches_played_last_season);
  if (season !== null && season > 0) return `≥ ${season} · last season`;
  const half = num(tender?.matches_played_last_half_season);
  if (half !== null && half > 0) return `≥ ${half} · last half season`;
  return null;
};

// Loupe experience cell for staff: "≥ 5 years" / "Not important" / null.
export const getExperienceLine = (tender) => {
  if (tender?.tender_category !== "staff") return null;
  if (tender?.experience_not_important) return "Not important";
  const years = num(tender?.experience_min_years);
  if (years !== null && years > 0) return `≥ ${years} years`;
  return null;
};

// Re-export so surfaces needing one-off checks share the same emptiness rule.
export { shouldRender };
